import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import AppShell from '../chrome/AppShell';
import { ConfirmDialog, Crumb, SectionCard } from '../components/forms';
import { PageHeadActions } from '../components/nav';
import { useList, useToast } from '../components/roster';
import useProduction from '@/src/Production/components/hooks/useProduction';
import '../calendar.css';

/*
 * Jobs settings — team ICS feed, active feed tokens, integration status.
 * Load is GET-only; the team feed is created (POST) only on click.
 */

const toWebcal = (url: string) => url.replace(/^https?/, 'webcal');
const toGoogleCal = (url: string) =>
    `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(toWebcal(url))}`;

const since = (value: any) => {
    if (!value) return '';
    const d = parseISO(String(value));
    return Number.isNaN(d.getTime()) ? '' : `since ${format(d, 'MMM d')}`;
};

const feedName = (f: any) =>
    f.subjectType === 'team'
        ? 'Team feed'
        : [f.firstName, f.lastName].filter(Boolean).join(' ') || 'Staff feed';

const feedPhrase = (f: any) => (f.subjectType === 'team' ? 'the team feed' : `${feedName(f)}’s feed`);

export default function JobsSettingsV3() {
    const navigate = useNavigate();
    const { getTeamCalendarFeed, getCalendarFeeds, revokeCalendarFeed, getIntegrationsStatus } = useProduction();
    const toast = useToast();

    const feeds = useList<any>(() => getCalendarFeeds());
    const [status, setStatus] = useState<any | null | 'failed'>(null);
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState(false);
    const [copied, setCopied] = useState<'yes' | 'failed' | null>(null);
    const [revoking, setRevoking] = useState<any | null>(null);
    const [busyId, setBusyId] = useState<number | null>(null);
    const busy = useRef(false);

    const loadStatus = () => {
        setStatus(null);
        getIntegrationsStatus()
            .then((s) => setStatus(s ?? 'failed'))
            .catch(() => setStatus('failed'));
    };
    useEffect(() => {
        loadStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const teamFeed = (feeds.rows ?? []).find((f: any) => f.subjectType === 'team' && f.url);

    const createFeed = async () => {
        if (busy.current) return;
        busy.current = true;
        setCreating(true);
        setCreateError(false);
        try {
            const created = await getTeamCalendarFeed();
            if (!created?.url) setCreateError(true);
            feeds.reload();
        } catch {
            setCreateError(true);
        } finally {
            busy.current = false;
            setCreating(false);
        }
    };

    const copyLink = async () => {
        if (!teamFeed?.url) return;
        try {
            await navigator.clipboard.writeText(teamFeed.url);
            setCopied('yes');
        } catch {
            setCopied('failed');
        }
        window.setTimeout(() => setCopied(null), 2000);
    };

    const revoke = async (f: any) => {
        if (busy.current) return;
        busy.current = true;
        setBusyId(f.id);
        try {
            await revokeCalendarFeed(+f.id);
            toast.show('Feed revoked.');
        } catch {
            toast.show(`Couldn’t revoke ${feedPhrase(f)}. Try again.`);
        } finally {
            busy.current = false;
            setBusyId(null);
            feeds.reload();
        }
    };

    const integration = (label: string, on: boolean | undefined, detail: React.ReactNode) => (
        <div className="trow2">
            <span style={{ minWidth: 0, flex: 1 }}>
                <span className="nm2">{label}</span>
                <br />
                <span className="sub2">{detail}</span>
            </span>
            <span className="right" style={{ color: on ? 'var(--good)' : 'var(--warn)' }}>
                {on ? 'Connected' : 'Not configured'}
            </span>
        </div>
    );

    return (
        <AppShell>
            <Crumb label="Pipeline" onClick={() => navigate('/pipeline')} />
            <PageHeadActions title="Jobs settings" sub="Calendar feeds and integrations" />
            <div className="dets">
                <div className="stack">
                    <SectionCard title="Team calendar">
                        <p className="pg-sub" style={{ margin: '0 0 14px' }}>
                            Every production (and pending pipeline deals) syncs to your calendar automatically.
                        </p>
                        {feeds.failed ? (
                            <>
                                <p className="quiet-empty" style={{ marginBottom: 10 }}>Couldn’t load the feed link.</p>
                                <button type="button" className="tb-btn" onClick={feeds.reload}>
                                    Retry
                                </button>
                            </>
                        ) : feeds.rows === null ? (
                            <p className="quiet-empty">Getting the feed link…</p>
                        ) : teamFeed ? (
                            <div className="feed-actions" style={{ maxWidth: 360 }}>
                                <a className="tb-new" href={toWebcal(teamFeed.url)}>
                                    Add to Apple Calendar / Outlook
                                </a>
                                <a className="tb-btn" href={toGoogleCal(teamFeed.url)} target="_blank" rel="noreferrer">
                                    Add to Google Calendar
                                </a>
                                <button type="button" className="tb-btn" onClick={copyLink}>
                                    {copied === 'yes' ? 'Copied' : copied === 'failed' ? 'Couldn’t copy' : 'Copy feed link'}
                                </button>
                            </div>
                        ) : (
                            <>
                                <p className="quiet-empty" style={{ marginBottom: 10 }}>No team feed link yet.</p>
                                <button type="button" className="tb-new" onClick={createFeed} disabled={creating}>
                                    {creating ? 'Creating…' : 'Create feed link'}
                                </button>
                                {createError && (
                                    <p className="field-e" role="alert">
                                        Couldn’t create the feed link. Try again in a moment.
                                    </p>
                                )}
                            </>
                        )}
                        <p className="pg-sub" style={{ margin: '14px 0 0', fontSize: 'var(--fs-xs)' }}>
                            Anyone with the link can see the schedule.
                        </p>
                    </SectionCard>

                    <SectionCard title="Active feeds" note={feeds.rows ? String(feeds.rows.length) : undefined}>
                        {feeds.failed ? (
                            <p className="quiet-empty">Couldn’t load feeds.</p>
                        ) : feeds.rows === null ? (
                            <p className="quiet-empty">Loading…</p>
                        ) : feeds.rows.length === 0 ? (
                            <p className="quiet-empty">None yet.</p>
                        ) : (
                            feeds.rows.map((f: any) => (
                                <div className="trow2" key={f.id}>
                                    <span style={{ minWidth: 0, flex: 1 }}>
                                        <span className="nm2">{feedName(f)}</span>
                                        <br />
                                        <span className="sub2" style={{ fontFamily: 'var(--mono)' }}>
                                            …{String(f.token ?? '').slice(-10)}.ics
                                        </span>
                                        {since(f.createdAt) && <span className="sub2"> · {since(f.createdAt)}</span>}
                                    </span>
                                    <button
                                        type="button"
                                        className="tb-btn danger-soft"
                                        disabled={busyId !== null}
                                        aria-label={`Revoke ${feedName(f)}`}
                                        onClick={() => setRevoking(f)}
                                    >
                                        {busyId === f.id ? 'Revoking…' : 'Revoke'}
                                    </button>
                                </div>
                            ))
                        )}
                        <p className="pg-sub" style={{ margin: '12px 0 0', fontSize: 'var(--fs-xs)' }}>
                            Revoking a feed breaks that subscription everywhere it was added.
                        </p>
                    </SectionCard>

                    <SectionCard title="Integrations">
                        {status === 'failed' ? (
                            <>
                                <p className="quiet-empty" style={{ marginBottom: 10 }}>Couldn’t load integration status.</p>
                                <button type="button" className="tb-btn" onClick={loadStatus}>
                                    Retry
                                </button>
                            </>
                        ) : status === null ? (
                            <p className="quiet-empty">Loading…</p>
                        ) : (
                            <>
                                {integration(
                                    'Attio',
                                    status.attioConfigured,
                                    <span style={{ fontFamily: 'var(--mono)' }}>POST {status.webhookPath ?? ''}</span>,
                                )}
                                {integration(
                                    'Google Maps',
                                    status.googleMapsConfigured,
                                    'Timezone lookups; Places autocomplete needs a browser key',
                                )}
                            </>
                        )}
                    </SectionCard>
                </div>
            </div>

            <ConfirmDialog
                open={revoking !== null}
                title={revoking ? `Revoke ${feedPhrase(revoking)}?` : 'Revoke feed?'}
                body="Every calendar subscribed to it stops updating. This can't be undone."
                actionLabel="Revoke feed"
                onConfirm={() => revoking && revoke(revoking)}
                onClose={() => setRevoking(null)}
            />
            {toast.node}
        </AppShell>
    );
}
